import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { LogOut, Building2, ChevronDown } from 'lucide-react'

export function Header() {
  const { user, profile, signOut } = useAuth()
  const { currentTenant, tenants, setCurrentTenant, isPlatformAdmin } = useTenant()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [logoError, setLogoError] = useState(false)

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* ロゴ */}
          <div className="flex items-center gap-3">
            {!logoError ? (
              <img
                src="/logo.png"
                alt="認定資格.com"
                className="h-10 object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="font-semibold text-gray-900 tracking-tight text-lg">認定資格.com</span>
            )}
            {isPlatformAdmin && (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full ml-2">
                管理者
              </span>
            )}
          </div>

          {/* ユーザー情報 */}
          {user && (
            <div className="flex items-center gap-3">
              {/* テナント選択 */}
              {tenants.length > 1 && (
                <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <select
                    value={currentTenant?.tenant_id || ''}
                    onChange={(e) => {
                      const tenant = tenants.find((t) => t.tenant_id === e.target.value)
                      setCurrentTenant(tenant || null)
                    }}
                    className="text-sm bg-transparent border-none focus:outline-none focus:ring-0 text-gray-700 font-medium cursor-pointer"
                  >
                    {tenants.map((tenant) => (
                      <option key={tenant.tenant_id} value={tenant.tenant_id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* ユーザーメニュー */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                    {(profile?.full_name || user.email || '?')[0].toUpperCase()}
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                </button>

                {/* ドロップダウンメニュー */}
                {userMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setUserMenuOpen(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {profile?.full_name || 'ユーザー'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 truncate" title={user.email}>
                          {user.email}
                        </p>
                      </div>

                      {/* Mobile Tenant Selector */}
                      {tenants.length > 1 && (
                        <div className="md:hidden px-4 py-3 border-b border-gray-100">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">
                            テナント
                          </label>
                          <select
                            value={currentTenant?.tenant_id || ''}
                            onChange={(e) => {
                              const tenant = tenants.find((t) => t.tenant_id === e.target.value)
                              setCurrentTenant(tenant || null)
                              setUserMenuOpen(false)
                            }}
                            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#006cc6] focus:border-transparent"
                          >
                            {tenants.map((tenant) => (
                              <option key={tenant.tenant_id} value={tenant.tenant_id}>
                                {tenant.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Logout */}
                      <button
                        onClick={() => {
                          setUserMenuOpen(false)
                          handleSignOut()
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="font-medium">ログアウト</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
